import { useCallback } from 'react';

import { request } from '../../shared/api';
import { EmptyState, ListSkeleton, renderFailure } from '../../shared/states';
import { SummaryRow, type Summary } from '../../shared/SummaryRow';
import { useOpened } from '../../shared/use-opened';
import { usePage } from '../../shared/use-page';
import { ListingScreen } from '../listing/ListingScreen';
import { useUpgrade } from '../../shared/upgrade';

/**
 * Избранное (T11.10, `05_API.md` §7).
 *
 * ## Снятое объявление остаётся в списке
 *
 * С пометкой «снято», а не исчезнув. Сервер отдаёт его намеренно
 * (`modules/favorites/favorites.ts`), и прятать его здесь значило бы
 * потерять то самое, что человек отложил: он вернулся сравнить — а
 * машины нет, и непонятно, продали её или он сам её убрал.
 *
 * ## Убрать — сразу, без переспрашивания
 *
 * Избранное не удаляют «навсегда»: то же объявление кладётся обратно
 * одним нажатием на карточке. Подтверждение здесь стоило бы человеку
 * лишнего действия на каждой уборке ради ошибки ценой в одно нажатие.
 *
 * Строка убирается из списка сразу по ответу сервера, без перезагрузки
 * ленты: перезагрузка вернула бы человека в начало и подтянула бы
 * страницу заново ради одной исчезнувшей записи.
 */

/** Запись избранного в том виде, в каком её отдаёт `/v1/favorites`. */
interface Favorite {
  readonly favorite_id: string | number;
  readonly favorited_at: string;
  readonly listing: Summary;
}

/** Сколько записей за раз. Хватает на два экрана прокрутки. */
const PAGE = 20;

export function FavoritesScreen(): React.JSX.Element {
  const page = usePage<Favorite>('/v1/favorites', PAGE);
  const { opened, open, back, flow } = useOpened();
  const { drop } = page;

  const remove = useCallback(
    (listingId: string | number) => {
      const id = String(listingId);

      request(`/v1/favorites/by-listing/${id}`, { method: 'DELETE' })
        .then(() => {
          drop((one) => String(one.listing.listing_id) === id);
        })
        .catch(() => {
          // Строка осталась на месте: убрать её, не зная, убрал ли
          // сервер, значит показать человеку список, которого нет
        });
    },
    [drop],
  );

  return (
    <>
      {opened === null ? null : <ListingScreen listingId={opened} onBack={back} />}

      <div className={flow}>
        <Body page={page} onOpen={open} onRemove={remove} />
      </div>
    </>
  );
}

function Body({
  page,
  onOpen,
  onRemove,
}: {
  page: ReturnType<typeof usePage<Favorite>>;
  onOpen: (listingId: string | number) => void;
  onRemove: (listingId: string | number) => void;
}): React.JSX.Element {
  const onUpgrade = useUpgrade();

  if (page.state === 'loading') return <ListSkeleton rows={3} />;

  if (page.error !== null && page.items.length === 0) {
    return <>{renderFailure(page.error, { onRetry: page.reload, onUpgrade })}</>;
  }

  if (page.items.length === 0) {
    return (
      <EmptyState
        title="Избранного пока нет"
        hint="Откройте объявление и нажмите «В избранное» — здесь соберётся то, что вы сравниваете."
      />
    );
  }

  return (
    <>
      {page.items.map((one) => (
        <SummaryRow
          key={String(one.favorite_id)}
          listing={one.listing}
          onOpen={onOpen}
          action={{
            label: 'Убрать',
            onClick: () => {
              onRemove(one.listing.listing_id);
            },
          }}
        />
      ))}

      {page.hasMore ? (
        <button
          type="button"
          className="button"
          disabled={page.state === 'more'}
          onClick={page.more}
        >
          {page.state === 'more' ? 'Загружаю…' : 'Показать ещё'}
        </button>
      ) : null}
    </>
  );
}
